import {
	BrowserAuthError,
	type Configuration,
	InteractionRequiredAuthError,
	NavigationClient,
	PublicClientApplication,
	type RedirectRequest,
	type SilentRequest
} from '@azure/msal-browser'

import type { AuthenticationResult, IAuthService, MaybeAccount } from '@/services'
import { EntraIDLoginRequestScope } from '@/services/EntraIDAuth'
export type { Configuration }

export class EntraIDAuthService implements IAuthService {
	private config: Configuration
	constructor(config: Configuration) {
		this.config = config
		this.msal = new PublicClientApplication(config)
	}

	private token: string | null = null
	private msal: PublicClientApplication

	get isAuthenticated(): boolean {
		return this.msal.getActiveAccount() !== null
	}

	async initialize(client?: NavigationClient): Promise<MaybeAccount> {
		await this.msal
			.handleRedirectPromise()
			.then((tokenResponse) => {
				if (tokenResponse) {
					// console.debug('Returning from a Redirect that was successful')
				} else {
					console.debug('The application is not returning from a Redirect.')
				}
			})
			.catch((error) => {
				console.error(error)
			})

		if (client) {
			this.msal.setNavigationClient(client)
		}

		const accounts = this.msal.getAllAccounts()
		console.debug('MSAL Accounts found', accounts)
		if (accounts?.length) {
			this.setAccount(accounts[0])
		}

		return this.msal.getActiveAccount()
	}

	async login(): Promise<MaybeAccount> {
		// console.debug('Calling login')
		const request: RedirectRequest = {
			redirectUri: this.config.auth.redirectUri,
			scopes: EntraIDLoginRequestScope
		}
		// console.debug('Calling loginRedirect')
		return this.msal
			.loginRedirect(request)
			.then(() => {
				// console.log('Logged in with', result)
				const currentAccounts = this.msal.getAllAccounts()
				if (currentAccounts?.length) {
					this.setAccount(currentAccounts[0])
					return currentAccounts[0]
				} else {
					throw new Error('No accounts found.')
				}
			})
			.catch((error: BrowserAuthError) => {
				if (error.errorCode === 'interaction_in_progress') {
					this.reset()
					return this.login()
				}
				console.log(error)
				throw new Error(error.errorMessage)
			})
	}

	logout(): Promise<void> {
		// console.debug('Calling logout')
		return this.msal.logoutRedirect({})
	}

	async getToken(scopes: string[] = ['User.Read']): Promise<AuthenticationResult> {
		// console.debug('Calling getToken')
		const request: SilentRequest = {
			scopes: scopes
		}
		return (
			this.msal
				// try getting the token silently
				.acquireTokenSilent(request)
				// attempt login popup if this fails
				.catch(async (error: unknown) => {
					// console.error('Error getting token silently', error)
					if (error instanceof InteractionRequiredAuthError) {
						await this.msal.acquireTokenRedirect(request).then(() => {
							// console.info('result', result)
							return this.msal.handleRedirectPromise()
						})
					}
					throw error
				})
				.then((result: AuthenticationResult | null) => {
					//console.info('result', result)
					if (result !== null) {
						return result
					}
					return {} as AuthenticationResult
				})
		)
	}

	private setAccount(account: MaybeAccount): MaybeAccount {
		// console.debug('Calling setAccount')
		this.msal.setActiveAccount(account)
		return account
	}

	reset() {
		// console.debug('Calling reset')
		sessionStorage.clear()
	}
}
